import React, {
    ChangeEvent,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    Icon,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { DefaultHubButton } from '../../../HubLayout/components/Utilities/DefaultHubButton';
import { postReleaseImport } from '../../../AGSS/api/AGSSApi';
import { AxiosError, CancelToken } from 'axios';
import { usePromise } from '../../../Common/hooks/usePromise';
import { getThemeFromString } from '../../../Common/util/localStorageUtils';
import { ThemeContext } from '../../../HubLayout/models/ThemeContext';
import { getReleaseDetailsPath } from '../../../AGSS/util/ReleaseUtils';
import {
    GlobalConfigProperties,
    getConfig,
} from '../../../Common/api/ABHub/ABHubGlobalConfig';
import { useHistory } from 'react-router-dom';
import { sanitizeRedirectUrl } from '../../../Common/util/urlValidation';

interface IReleaseImporterProps {}

export const ReleaseImporter: React.FC<IReleaseImporterProps> = (
    props: IReleaseImporterProps
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const history = useHistory();

    const environment = getConfig(GlobalConfigProperties.Environment);

    const stackTokens = { childrenGap: 16 };
    const [validEnv, setValidEnv] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File | undefined>(
        undefined
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showResult, setShowResult] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Validate environment - only allow imports in development/test environments
    useEffect(() => {
        if (
            environment === 'Development' ||
            environment === 'public.dev' ||
            environment === 'Test' ||
            environment === 'public.sovmock'
        ) {
            setValidEnv(true);
        } else {
            setValidEnv(false);
        }
    }, [environment]);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setSelectedFile(event.target.files[0]);
            setErrorMessage(null);
            setSuccessMessage(null);
        }
    };

    const openFilePicker = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const importRelease = useCallback(
        async (cancelToken?: CancelToken) => {
            if (selectedFile) {
                try {
                    setErrorMessage(null);
                    setSuccessMessage(null);
                    const result = await postReleaseImport(
                        selectedFile,
                        cancelToken
                    );
                    return result;
                } catch (error) {
                    const responseData = (
                        error as AxiosError<string | { message?: string }>
                    ).response?.data;
                    const message =
                        typeof responseData === 'string'
                            ? responseData
                            : responseData?.message ?? (error as Error).message;
                    setErrorMessage(`Import failed: ${message}`);
                    throw error;
                }
            } else {
                setErrorMessage('No file selected');
            }
        },
        [selectedFile]
    );

    const [result, error, isLoaded, start, cancel] = usePromise(
        importRelease,
        false
    );

    const handleImportClick = () => {
        setIsLoading(true);
        start();
    };

    const handleCancelClick = () => {
        setSelectedFile(undefined);
        cancel();
    };

    const parseLinesAndTabs = (value: string) =>
        value.replace(/(\\n)/g, '<br>').replace(/(\\t)/g, '&nbsp;');

    useEffect(() => {
        if (result) {
            setIsLoading(false);
            setSelectedFile(undefined);
            setSuccessMessage('Release imported successfully');
        } else if (error) {
            setIsLoading(false);
            setSelectedFile(undefined);
            if (!errorMessage) {
                setErrorMessage('Release import failed');
            }
        }
    }, [result, error]);

    return (
        <div className="ReleaseImporter-root">
            {!validEnv && (
                <MessageBar
                    messageBarType={MessageBarType.warning}
                    isMultiline={true}
                >
                    <strong>Release Import Disabled</strong>
                    <br />
                    Release import is only available in development and test
                    environments. Current environment:{' '}
                    <strong>{environment}</strong>
                </MessageBar>
            )}
            {validEnv && (
                <Stack tokens={stackTokens}>
                    {errorMessage && (
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={true}
                            onDismiss={() => setErrorMessage(null)}
                            dismissButtonAriaLabel="Close"
                        >
                            {errorMessage}
                        </MessageBar>
                    )}
                    {successMessage && (
                        <MessageBar
                            messageBarType={MessageBarType.success}
                            isMultiline={false}
                            onDismiss={() => setSuccessMessage(null)}
                            dismissButtonAriaLabel="Close"
                        >
                            {successMessage}
                        </MessageBar>
                    )}

                    {!selectedFile && (
                        <Stack>
                            <Text
                                variant="medium"
                                styles={{ root: { marginBottom: '8px' } }}
                            >
                                Select a release file to import
                            </Text>
                            <DefaultHubButton
                                buttonText="Select Import File"
                                icon="FileCode"
                                onClick={openFilePicker}
                            />

                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                        </Stack>
                    )}

                    {selectedFile && (
                        <Stack>
                            <MessageBar
                                messageBarType={MessageBarType.info}
                                isMultiline={false}
                            >
                                Selected File:{' '}
                                <strong>{selectedFile?.name}</strong>
                            </MessageBar>
                            {!isLoading ? (
                                <Stack horizontal tokens={stackTokens}>
                                    <DefaultHubButton
                                        buttonText="Import Release"
                                        icon="CloudUpload"
                                        onClick={handleImportClick}
                                        disabled={!selectedFile}
                                    />

                                    <DefaultHubButton
                                        buttonText="Cancel"
                                        icon="Cancel"
                                        fontColor={
                                            theme.semanticColors.errorIcon
                                        }
                                        borderColor={
                                            theme.semanticColors.errorIcon
                                        }
                                        onClick={handleCancelClick}
                                    />
                                </Stack>
                            ) : (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        padding: '24px',
                                    }}
                                >
                                    <Spinner
                                        size={SpinnerSize.large}
                                        label="Importing release..."
                                    />
                                </div>
                            )}
                        </Stack>
                    )}

                    {result && !selectedFile && (
                        <Stack tokens={stackTokens}>
                            <DefaultHubButton
                                buttonText="View Release"
                                icon="View"
                                onClick={() => {
                                    // Validate the redirect URL to prevent unvalidated redirects (SM01507)
                                    const releasePath = getReleaseDetailsPath(
                                        result.data ?? ''
                                    );
                                    const safePath = sanitizeRedirectUrl(
                                        releasePath,
                                        '/'
                                    );
                                    history.push(safePath);
                                }}
                            />

                            {showResult ? (
                                <Stack tokens={stackTokens}>
                                    <DefaultHubButton
                                        buttonText="Hide Import Result"
                                        icon="Hide"
                                        onClick={() => setShowResult(false)}
                                    />
                                    <TextField
                                        readOnly
                                        multiline
                                        type="text"
                                        resizable
                                        value={parseLinesAndTabs(
                                            result.message ?? ''
                                        )}
                                    />
                                </Stack>
                            ) : (
                                <DefaultHubButton
                                    buttonText="Show Import Result"
                                    icon="EntryView"
                                    onClick={() => setShowResult(true)}
                                />
                            )}
                        </Stack>
                    )}
                </Stack>
            )}
        </div>
    );
};
